import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowUpRight } from "lucide-react";
import { AGENTS, AGENT_FILTERS } from "../data";
import { Chip, Label, StatusDot, MetaRow, Reveal, ComingSoonBox } from "../components/primitives";

const riskTone = (r) => (r === "Low" ? "green" : r === "High" ? "gold" : "muted");

const AgentModal = ({ agent, onClose }) => {
  const [perms, setPerms] = useState({ ...agent.permissions });
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end justify-center bg-ink-900/80 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      data-testid="agent-modal-backdrop"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] w-full max-w-2xl overflow-y-auto border border-white/[0.1] bg-panel"
        data-testid="agent-modal"
      >
        <div className="flex items-center justify-between border-b border-white/[0.08] px-6 py-4">
          <div>
            <Label>Agent</Label>
            <h3 className="mt-1 font-mono text-xl font-bold tracking-[0.1em] text-fog-100">{agent.id}</h3>
          </div>
          <button onClick={onClose} data-testid="agent-modal-close"
            className="flex h-9 w-9 items-center justify-center border border-white/10 text-fog-400 transition-colors hover:text-fog-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-0 sm:grid-cols-2">
          <div className="border-b border-white/[0.08] p-6 sm:border-r">
            <MetaRow k="Status" v={agent.status} accent={agent.status === "ACTIVE"} />
            <MetaRow k="Role" v={agent.role} />
            <MetaRow k="Risk" v={agent.risk} />
            <MetaRow k="Specialization" v={agent.spec} />
            <p className="mt-4 text-xs leading-relaxed text-fog-400">{agent.desc}</p>
            <Label className="mb-2 mt-6">Capabilities</Label>
            <div className="flex flex-wrap gap-1.5">
              {agent.capabilities.map((c) => (
                <span key={c} className="border border-white/10 bg-ink-900 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-fog-400">{c}</span>
              ))}
            </div>
          </div>

          <div className="p-6">
            <Label className="mb-1" tone="gold">Permissions — UI Demonstration</Label>
            {[["Max Position %", "maxPosition", 50], ["Max Trade $", "maxTrade", 2000], ["Max Drawdown %", "maxDrawdown", 25]].map(([label, key, max]) => (
              <div key={key} className="mt-4">
                <div className="mb-1.5 flex justify-between font-mono text-[10px] uppercase tracking-[0.18em]">
                  <span className="text-fog-600">{label}</span>
                  <span className="text-pulse-soft">{perms[key]}</span>
                </div>
                <input
                  type="range" min="0" max={max} value={perms[key]}
                  onChange={(e) => setPerms({ ...perms, [key]: +e.target.value })}
                  data-testid={`perm-slider-${key}`}
                  className="syn-slider w-full"
                />
              </div>
            ))}
            <MetaRow k="Allowed Assets" v={agent.permissions.allowed} />
            <ComingSoonBox label="Execution" className="mt-6" />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function Agents() {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const list = useMemo(() => {
    return AGENTS.filter((a) => {
      const okFilter = filter === "All" || a.category === filter;
      const q = query.trim().toLowerCase();
      const okQuery = !q || a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q) || a.spec.toLowerCase().includes(q);
      return okFilter && okQuery;
    });
  }, [filter, query]);

  return (
    <main className="pt-16" data-testid="agents-page">
      <div className="grid-bg border-b border-white/[0.06]">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-20">
          <Reveal>
            <div className="flex items-center gap-3"><StatusDot /><Label tone="green">Specialized intelligence for autonomous finance</Label></div>
            <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-fog-100 sm:text-5xl">AGENT REGISTRY</h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-fog-400 sm:text-base">
              Discover and deploy specialized financial agents. All agents shown are protocol concepts — no live performance is claimed.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1.5" data-testid="agent-filters">
            {AGENT_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.toLowerCase()}`}
                className={`border px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-200 ${
                  filter === f
                    ? "border-pulse/50 bg-pulse/10 text-pulse-soft"
                    : "border-white/10 text-fog-400 hover:border-white/25 hover:text-fog-100"}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="relative lg:w-72">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fog-600" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search agents…"
              data-testid="agent-search"
              className="w-full border border-white/10 bg-panel py-2.5 pl-9 pr-3 font-mono text-xs text-fog-100 placeholder:text-fog-600 focus:border-pulse/50 focus:outline-none"
            />
          </div>
        </div>

        <div className="mb-8 flex justify-end">
          <Link to="/leaderboard" data-testid="agents-leaderboard-link"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog-600 transition-colors hover:text-brass-bright">
            Agent Performance Leaderboard →
          </Link>
        </div>

        {list.length === 0 ? (
          <div className="border border-dashed border-white/10 py-20 text-center font-mono text-xs uppercase tracking-[0.2em] text-fog-600" data-testid="agents-empty">
            No agents match this query
          </div>
        ) : (
          <div className="grid gap-px border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2 xl:grid-cols-3">
            {list.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04, duration: 0.4 }}>
                <button
                  onClick={() => setSelected(a)}
                  data-testid={`agent-card-${a.id.toLowerCase()}`}
                  className="group h-full w-full bg-panel p-5 text-left transition-colors duration-300 hover:bg-elevated"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-mono text-base font-bold tracking-[0.1em] text-fog-100">{a.name}</span>
                      <span className="mt-0.5 block font-mono text-[10px] tracking-[0.18em] text-fog-600">{a.id}</span>
                    </div>
                    <Chip tone={a.status === "ACTIVE" ? "green" : "muted"}>
                      <StatusDot tone={a.status === "ACTIVE" ? "green" : "muted"} /> {a.status}
                    </Chip>
                  </div>
                  <div className="mt-5 space-y-1.5">
                    <MetaRow k="Role" v={a.role} />
                    <MetaRow k="Risk" v={a.risk} />
                    <MetaRow k="Strategy" v={a.spec} />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {a.capabilities.slice(0, 3).map((c) => (
                      <span key={c} className="border border-white/[0.08] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-fog-600">{c}</span>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center gap-2 border-t border-white/[0.06] pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-fog-600 transition-colors group-hover:text-pulse-soft">
                    Inspect Agent <ArrowUpRight className="h-3 w-3" />
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && <AgentModal agent={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </main>
  );
}
